import React, { ReactElement, useMemo, useRef, useState } from 'react'
import styled from '~/app/components/styled'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import yaLogo from '~/app/imgs/empty-thumnail-yanolja-h-56.svg?inline'
import {
  OrderCancelPolicyDomestic,
  OrderCancelPolicyLeisureItem,
  OrderCancelPolicyTrainTicket,
} from '~/app/components/order-cancel/hooks/useOrderCancelPolicy'
import { DefaultBusLogo } from '~/app/components/order-list/common/OrderCardItem'
import { useBusTransportationCompanyQuery } from '~/__generated__/app/query/order-details/BusTransportationCompany.graphql'
import isBusImageSkip from '~/app/utils/isBusImageSkip'
import { isTypeOf } from '~/app/utils'
const isDomesticOrder = isTypeOf<OrderCancelPolicyDomestic>('OrderCancelPolicyDomestic')
const isLeisureOrder = isTypeOf<OrderCancelPolicyLeisureItem>('OrderCancelPolicyLeisureProductItem')
const isTrainOrder = isTypeOf<OrderCancelPolicyTrainTicket>('OrderCancelPolicyTrainTicket')
import {
  CancelPolicyBusTicket,
  CancelPolicyType,
  CategoryType,
  CancelPolicy,
} from '~/__generated__/__types__'
import ConfigBadgeGroup from '~/app/components/common/Badge/ConfigBadgeGroup'
import { ConfigBadgeWrapper } from '~/app/components/common/Badge/Badge.styles'
import FixedTooltip from '~/app/components/common/Tooltip/FixedTooltip'
import krNumberToString from '~/app/utils/krNumberToString'
import OrderCancelLeisureCancelPolicy from '~/app/components/order-cancel/orderItems/LeisureCancelPolicy'
import useCancelCommission from '~/app/components/order-list/hooks/useCancelCommission'
import CancelPolicyText from '~/app/components/common/CancelPolicy/CancelPolicyText'
import { TConvertedProductInfo } from '~/app/components/order-converter/converter/DomesticOrderConverter'
import InfoLineToSpan from '~/app/components/common/InfoLineToSpan'
import { Icon_info } from '@yanolja-rnd/ya-com-system-icon'
import Checkbox from '~/app/components/common/Checkbox/YaComCheckbox'
import { Typography } from '@yanolja-rnd/ya-com-core'
import LeisureOptionTitle from '~/app/components/order-list/common/LeisureOptionTitle'
const Container = styled.div`
  position: relative;
  padding: 2rem 0;

  &.responsive {
    border-bottom: solid 1px ${({ theme }) => theme.color.gray200};

    :nth-last-of-type(1) {
      border-bottom: none;
    }

    @media (min-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
      width: 50%;
      :nth-of-type(2n - 1) {
        border-right: solid 0.1rem ${({ theme }) => theme.color.gray200};
      }
      :nth-last-of-type(1) {
        border-bottom: none;
      }
      :nth-last-of-type(2) {
        :nth-of-type(2n - 1) {
          border-bottom: none;
        }
      }
    }
  }
`
const TitleContainer = styled.div`
  margin-bottom: 0.8rem;

  .title {
    ${Typography('subtitle3').bold}
    word-break: break-word;
    color: ${({ theme }) => theme.color.gray900};
  }
`
const Sku = styled.span`
  display: flex;
`
const CheckContainer = styled.div`
  margin-right: 1.2rem;
`
const ItemCheckbox = styled(Checkbox)`
  img {
    width: 2rem;
    height: 2rem;
  }
`
const ItemContainer = styled.div`
  display: flex;
  width: calc(100% - 32px);
`
const ImageContainer = styled.div`
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 0.4rem;
  background-color: ${({ theme }) => theme.color.gray50};
  margin-right: 0.8rem;
`
const ContentContainer = styled.div`
  max-width: calc(100% - 64px);

  > * {
    width: 100%;
    margin-top: 0.4rem;
    ${Typography('body2').regular}
    color: ${({ theme }) => theme.color.gray600};
    &:first-of-type {
      margin-top: 0;
    }
  }

  .subTitle {
    ${Typography('body1').bold}
    word-break: break-word;
    color: ${({ theme }) => theme.color.gray900};
  }

  .ratePlanTitle {
    ${Typography('body1').regular}
    color: ${({ theme }) => theme.color.gray900};
  }

  .info {
    margin-top: 0.8rem;
    display: grid;
    gap: 0.4rem;
  }
`
const Info = styled.div`
  ${Typography('body2').regular}
  color: ${({ theme }) => theme.color.gray600};
  word-break: break-word;

  .black {
    color: ${({ theme }) => theme.color.gray900};
  }
`

const Dim = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 4rem;
  opacity: 0.5;
  background-color: ${p => p.theme.color.white};
`

const Image = styled(LazyLoadImage)`
  position: relative;
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 0.4rem;
`

const SVGFrame = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 0.4rem;
  margin-right: 0.8rem;
  background: ${({ theme: { color } }) => color.gray50};
`

const PendingReservationTooltipContainer = styled.div`
  text-align: right;
`

const PendingReservationTooltipTitle = styled.p`
  display: flex;
  color: ${({ theme }) => theme.color.gray600};
  ${Typography('caption').regular};
`

const PendingReservationTooltipIcon = styled(Icon_info)`
  margin-left: 0.2rem;
  path {
    fill: ${({ theme }) => theme.color.gray600};
  }
`

const Price = styled.p`
  margin-top: 1.2rem;
  text-align: right;
  ${Typography('body1').regular}
  color: ${({ theme }) => theme.color.gray900};
`

const StateWrapper = styled.div<{ status: ItemState }>`
  ${Typography('body2').bold};
  padding-bottom: 0.3rem;
  text-align: right;
  color: ${({ theme }) => theme.color.gray600};

  > strong {
    ${({ status, theme }) => status === ItemState.UNCANCELABLE && `color: ${theme.color.yared}`};
  }
`

const CancelPolicyWrapper = styled.div`
  margin-top: 0.4rem;
`

const Options = styled.div`
  color: ${({ theme }) => theme.color.gray600};
  ${Typography('body2.multiline').regular}
  margin-bottom: 0.8rem;
  word-break: keep-all;
  padding: 0.6rem 1rem;
  border-radius: 0.6rem;
  background: ${({ theme }) => theme.color.gray50};
`

export enum ItemType {
  ACCOMMODATION = 'DomesticOrder',
  LEISURE = 'LeisureItem',
  TRAIN = 'TrainItem',
  BUS = 'BusItem',
}

export enum ItemState {
  CANCELABLE = 'cancelable',
  CANCELED = 'canceled',
  CANCEL_REQUESTED = 'cancel-requested',
  USED = 'used',
  UNCANCELABLE = 'uncancelable',
}

export type IOrderItem = {
  // 추가주문의 통합주문 번호
  compositeId?: string
  itemType: ItemType
  categoryType?: CategoryType
  orderItemId: string
  status: ItemState
  title: string
  subTitle: string
  optionTitle?: { title: string; caption?: string }
  ratePlanTitle?: string
  info: TConvertedProductInfo
  options?: string
  commissionAmount?: number
  price?: number
  productUrl?: string
  imageSrc?: string | ReactElement
  id: string | number
  // categoryOrders 의 id
  parentId?: string | number
  cancelPolicy?: CancelPolicy
  cancelPolicies?: OrderCancelPolicyDomestic['cancelPolicies']
  order?:
    | OrderCancelPolicyDomestic
    | OrderCancelPolicyLeisureItem
    | OrderCancelPolicyTrainTicket
    | CancelPolicyBusTicket
  transportationCompanyCode?: string
  isPendingReservation?: boolean
  badges?: string[]
}

interface IOrderItemProps extends IOrderItem {
  checked?: boolean
  showCheckbox?: boolean
  showPrice?: boolean
  onCheckedChange?: (id: string, checked: boolean) => void
  responsive?: boolean
  requestedAt: string
}
const Item: React.FC<IOrderItemProps> = props => {
  const {
    orderItemId,
    status,
    imageSrc = '',
    productUrl,
    title,
    subTitle,
    optionTitle,
    ratePlanTitle,
    info,
    options,
    commissionAmount,
    price,
    checked = false,
    showPrice = true,
    showCheckbox = true,
    responsive = false,
    cancelPolicy,
    order,
    transportationCompanyCode,
    isPendingReservation,
    badges,
    cancelPolicies,
    requestedAt,
  } = props
  const pendingReservationTooltipContent = useRef(
    <div>
      <p>대기 예약 상품은 예약이 확정되기 전까지는 무료로 취소할 수 있어요.</p>
      <p>단, 예약 확정 후에는 취소 요청 시점에 따라 취소 수수료가 부과될 수 있습니다.</p>
    </div>
  )

  const information = useMemo(
    () =>
      info?.map((line, index) => (
        <Info key={index}>
          <InfoLineToSpan line={line} />
        </Info>
      )),
    [info]
  )

  // 연박 계산을 위해 예약 시작을 정보를 전달하여 줌
  const [checkInAt = '', checkOutAt = ''] = useMemo(
    () =>
      order?.__typename === 'OrderCancelPolicyDomestic' ? [order.checkInAt, order.checkOutAt] : [],
    [order]
  )

  const showDim = useMemo(
    () => status !== ItemState.CANCELABLE || cancelPolicy?.type === CancelPolicyType.Total,
    [status, cancelPolicy]
  )

  const [imageLoadFail, setImageLoadFail] = useState(false)

  const LogoImage = useMemo(() => {
    if (typeof imageSrc === 'string') {
      const src = imageLoadFail ? yaLogo : (imageSrc as string)
      return (
        <Image
          alt={'상품 사진'}
          src={src}
          placeholderSrc={yaLogo}
          onError={() => setImageLoadFail(true)}
        />
      )
    } else {
      return <SVGFrame>{imageSrc}</SVGFrame>
    }
  }, [imageSrc, imageLoadFail])

  const busTransportationCompanyCode = transportationCompanyCode
  const { data: busTransportationCompanyData } = useBusTransportationCompanyQuery({
    variables: {
      code: transportationCompanyCode!,
    },
    skip: isBusImageSkip({ busTransportationCompanyCode, imageSrc }),
  })

  const cancelPolicySummary = useMemo(() => {
    const reason =
      isDomesticOrder(order) || isLeisureOrder(order) || isTrainOrder(order)
        ? order?.nonCancellationReason
        : order?.nonCancelableReason
    return {
      cancelable: order?.cancelable,
      nonCancellationReason: reason,
      cancelPolicy: cancelPolicy,
    }
  }, [order])

  const { cancelSummary } = useCancelCommission(cancelPolicySummary)
  const isLeisureCategory = useMemo(() => isLeisureOrder(order), [order])

  const propertyCategory = useMemo(() => {
    if (isDomesticOrder(order)) {
      return order.propertyCategory
    }
    return undefined
  }, [order])

  const isCancelable = useMemo(() => status === ItemState.CANCELABLE, [status])

  // 레저 취소 정책 노출 조건
  const showLeisureCancelPolicy = useMemo(
    () => isLeisureCategory && isCancelable,
    [isLeisureCategory, isCancelable]
  )

  // 취소 가능 취소 정책 노출 조건 (레저 아닌 경우)
  const showCancelableCancelPolicy = useMemo(
    () => !isLeisureCategory && isCancelable && !!cancelSummary,
    [isLeisureCategory, isCancelable, cancelSummary]
  )

  const statusTextMemo = useMemo(() => {
    switch (status) {
      case ItemState.CANCEL_REQUESTED:
        return '취소요청중'
      case ItemState.CANCELED:
        return '취소완료'
      case ItemState.USED:
        return '이용완료'
      case ItemState.UNCANCELABLE:
        return '취소 및 환불불가'
      default:
        return ''
    }
  }, [status])

  return (
    <Container className={responsive ? 'responsive' : ''}>
      <TitleContainer>
        {badges && (
          <ConfigBadgeWrapper className={'configBadge'}>
            <ConfigBadgeGroup badges={badges} />
          </ConfigBadgeWrapper>
        )}
        <div className={'title'}>
          <a href={productUrl}>{title}</a>
        </div>
      </TitleContainer>
      <Sku>
        {showCheckbox && (
          <CheckContainer>
            <ItemCheckbox
              checked={checked}
              disabled={status !== ItemState.CANCELABLE}
              onClick={() => props.onCheckedChange?.(orderItemId, !checked)}
            />
          </CheckContainer>
        )}
        <ItemContainer>
          <ImageContainer>
            {order?.__typename === 'CancelPolicyBusTicket' ? (
              imageSrc ? (
                LogoImage
              ) : (
                <DefaultBusLogo>
                  {busTransportationCompanyData?.busTransportationCompany?.name ?? ''}
                </DefaultBusLogo>
              )
            ) : (
              LogoImage
            )}
          </ImageContainer>
          <ContentContainer>
            <div className={'subTitle'}>{subTitle}</div>
            {optionTitle && <LeisureOptionTitle data={optionTitle} />}
            {ratePlanTitle && <div className={'ratePlanTitle'}>{ratePlanTitle}</div>}
            <div className={'info'}>{information}</div>
            {options && <Options>{options}</Options>}
          </ContentContainer>
        </ItemContainer>
      </Sku>
      {showDim && <Dim />}
      {showPrice && (
        <>
          <Price>
            <strong>{krNumberToString(price || 0)}</strong>원
          </Price>
          {showLeisureCancelPolicy && (
            <CancelPolicyWrapper>
              <OrderCancelLeisureCancelPolicy
                state={status}
                order={order as OrderCancelPolicyLeisureItem}
              />
            </CancelPolicyWrapper>
          )}
          {showCancelableCancelPolicy && (
            <CancelPolicyWrapper>
              <CancelPolicyText
                cancelPolicySummary={{
                  cancelPolicy,
                  commissionAmount,
                  multipleDaysCancelPolicyOption: {
                    cancelPolicies,
                    checkInAt,
                    checkOutAt,
                    currentAt: requestedAt,
                  },
                }}
                propertyCategory={propertyCategory}
                isPendingReservation={isPendingReservation}
              />
            </CancelPolicyWrapper>
          )}
          {!isCancelable && (
            <CancelPolicyWrapper>
              <StateWrapper status={status}>
                <strong>{statusTextMemo}</strong>
              </StateWrapper>
            </CancelPolicyWrapper>
          )}
        </>
      )}
      {isPendingReservation && (
        <PendingReservationTooltipContainer>
          <FixedTooltip
            content={pendingReservationTooltipContent.current}
            includeCloseIcon={true}
            stickyTo={'right'}
            customTooltipRight={0.1}
            placement={'bottom'}
            borderRadius={0.4}
          >
            <PendingReservationTooltipTitle>
              예약 확정 전까지
              <PendingReservationTooltipIcon size={1.4} unit="rem" />
            </PendingReservationTooltipTitle>
          </FixedTooltip>
        </PendingReservationTooltipContainer>
      )}
    </Container>
  )
}

export default Item
